import { ModalConfirmation, ModalConfirmationProps, notify, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../../config/queryClient'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useSegment } from '../../../../hooks'
import { useUpdateVatDeclarationSettlement } from '../../hooks/useUpdateVatDeclarationSettlement'
import { PeriodData } from '../../utils/getPeriodData'

interface VatDeclarationsVoidPeriodSettlementConfirmationModalProps extends ModalConfirmationProps {
  currentCorrection?: { correctionNo: number }
  periodData?: PeriodData
  vatDeclarationId: string
}

export const VatDeclarationsVoidPeriodSettlementConfirmationModal = ({
  vatDeclarationId,
  id,
  periodData,
  currentCorrection,
  ...rest
}: VatDeclarationsVoidPeriodSettlementConfirmationModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(id)
  const { track } = useSegment()

  const { update: updatePeriodSettlement, isProcessing } = useUpdateVatDeclarationSettlement({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.VatDeclarations)
      queryClient.invalidateQueries(QueryKeys.TaxReturnPeriods)
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsVoidSettlement,
        message: t('vat_declarations.void_settlement_modal.success'),
        variant: 'success',
      })

      if (periodData && currentCorrection) {
        track('VAT Declaration Voided (Client)', {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          period_settled: periodData.periodSettled,
          year: periodData.year,
          type: currentCorrection?.correctionNo > 0 ? 'correction' : 'normal',
        })
      }
    },
    onError: (error) => {
      closeModal()
      notify({
        id: NotificationKeys.VatDeclarationsVoidSettlement,
        message: error?.body?.errorMessage || t('vat_declarations.void_settlement_modal.fail'),
        variant: 'error',
      })
    },
  })

  const handleVoidSettlementClick = useCallback(() => {
    updatePeriodSettlement({ id: vatDeclarationId, isSettled: false })
  }, [updatePeriodSettlement, vatDeclarationId])

  return (
    <ModalConfirmation
      {...rest}
      id={id}
      cancelLabel={t('vat_declarations.void_settlement_modal.cancel')}
      danger
      message={t('vat_declarations.void_settlement_modal.message')}
      okLabel={t('vat_declarations.void_settlement_modal.approve')}
      okLoading={isProcessing}
      onOk={handleVoidSettlementClick}
      title={t('vat_declarations.void_settlement_modal.title')}
    />
  )
}
